import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationService } from '@/api/services/notificationService'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useAuthStore } from '@/store/useAuthStore'
import { useDebounce } from '@/shared/hooks'
import { cn } from '@/shared/utils'
import {
  Search, Download, Calendar, Bell, Send, X, Plus,
  ChevronLeft, ChevronRight, Clock, CheckCircle,
  Users,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDT(v?: string | null) {
  if (!v) return '—'
  try {
    const d = new Date(v)
    return d.toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric' }).replace(/\//g,'-')
      + ' – ' + d.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })
  } catch { return v }
}

// Pick a translation by locale, falling back to the flat fields
function pickTranslation(row: any, locale = 'en') {
  const tr = row?.translations?.find((tl: any) => tl?.locale?.toLowerCase() === locale)
    ?? row?.translations?.[0]
  return {
    name: tr?.name ?? row?.title ?? '—',
    description: tr?.description ?? row?.description ?? row?.data?.description ?? '',
  }
}

// "trainer_update_starting_session" → "Trainer update starting session"
function prettyEvent(v?: string) {
  if (!v) return '—'
  const s = v.replace(/_/g, ' ').trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ── Notification card ─────────────────────────────────────────────────────────
function NotificationCard({ row }: { row: any }) {
  const { t } = useTranslation()
  const { name: title, description: message } = pickTranslation(row, 'en')
  const createdAt   = row.created_at
  const event       = prettyEvent(row.trigger_event)
  const isRead      = !!row.read_at
  const recipient   = row.user_id ? t('notificationPage.card.userHash', { id: row.user_id }) : '—'
  const relatedName = row.data?.trainer_details?.name ?? row.data?.student_details?.name ?? null

  const statusCls = isRead
    ? 'bg-green-100 text-green-700 border-green-200'
    : 'bg-brand-100 text-brand-700 border-brand-200'

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">

      {/* Key Info header */}
      <div className="px-4 pt-4 pb-2">
        <div className="text-[12px] font-bold text-gray-400 uppercase mb-2">{t('notificationPage.card.keyInfo')}</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Users size={11} className="text-gray-500"/>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('notificationPage.card.recipient')}</div>
              <div className="text-xs font-bold text-gray-800 truncate max-w-28">{relatedName ?? recipient}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Bell size={11} className="text-gray-500"/>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('notificationPage.card.event')}</div>
              <div className="text-xs font-bold text-gray-800 truncate max-w-28">{event}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Calendar size={11} className="text-gray-500"/>
            </div>
            <div>
              <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('notificationPage.card.createdAt')}</div>
              <div className="text-[12px] font-semibold text-gray-700">{fmtDT(createdAt)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Clock size={11} className="text-gray-500"/>
            </div>
            <div>
              <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('notificationPage.card.readAt')}</div>
              <div className="text-[12px] font-semibold text-gray-700">
                {row.read_at ? fmtDT(row.read_at) : t('notificationPage.card.notRead')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-4 border-t border-gray-100 my-2"/>

      {/* Title */}
      <div className="px-4 pb-1">
        <div className="text-[12px] font-bold text-gray-400 uppercase mb-1">{t('notificationPage.card.title')}</div>
        <div className="text-sm font-bold text-gray-900 leading-snug">{title}</div>
      </div>

      {/* Message */}
      {message && (
        <div className="px-4 pb-3">
          <div className="text-[12px] font-bold text-gray-400 uppercase mb-1">{t('notificationPage.card.message')}</div>
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{message}</p>
        </div>
      )}

      {/* Status */}
      <div className="px-4 pb-4">
        <span className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold',
          statusCls
        )}>
          {isRead ? <><CheckCircle size={11}/> {t('notificationPage.card.read')}</> : <><Clock size={11}/> {t('notificationPage.card.unread')}</>}
        </span>
      </div>
    </div>
  )
}

// ── Sent message row (school-admin outbox style) ───────────────────────────────
function SentMessageRow({ row }: { row: any }) {
  const { t } = useTranslation()
  const { name: title, description: message } = pickTranslation(row, 'en')
  const recipient = row.data?.trainer_details?.name
    ?? (row.user_id ? t('notificationPage.sentRow.trainerHash', { id: row.user_id }) : t('notificationPage.sentRow.allTrainers'))
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
            <Send size={14} className="text-brand-600"/>
          </div>
          <div className="min-w-0">
            <div className="text-[12px] text-gray-400 uppercase font-semibold tracking-wide">{t('notificationPage.sentRow.to')}</div>
            <div className="text-sm font-bold text-gray-800 truncate">{recipient}</div>
          </div>
        </div>
        <div className="text-[13px] text-gray-400 flex items-center gap-1 flex-shrink-0 mt-1">
          <Calendar size={11}/> {fmtDT(row.created_at)}
        </div>
      </div>
      <div className="mt-3 ps-[46px]">
        <div className="text-sm font-bold text-gray-900 leading-snug">{title}</div>
        {message && <p className="text-xs text-gray-600 leading-relaxed mt-1">{message}</p>}
      </div>
    </div>
  )
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 animate-pulse overflow-hidden">
      <div className="p-4 space-y-3">
        <div className="h-2 bg-gray-100 rounded w-16"/>
        <div className="grid grid-cols-2 gap-3">
          {[1,2,3,4].map(i=><div key={i} className="h-8 bg-gray-100 rounded-lg"/>)}
        </div>
        <div className="border-t border-gray-100"/>
        <div className="h-3 bg-gray-200 rounded w-2/3"/>
        <div className="space-y-1">
          <div className="h-2 bg-gray-100 rounded w-full"/>
          <div className="h-2 bg-gray-100 rounded w-4/5"/>
          <div className="h-2 bg-gray-100 rounded w-3/5"/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="h-8 bg-gray-100 rounded-xl"/>
          <div className="h-8 bg-gray-100 rounded-xl"/>
        </div>
      </div>
    </div>
  )
}

// ── Send Notification Modal ───────────────────────────────────────────────────
function CreateNotificationModal({
  open, onClose, onSuccess, editRow,
}: {
  open: boolean; onClose: () => void; onSuccess: () => void; editRow?: any
}) {
  const { t }          = useTranslation()
  const currentUser   = useAuthStore((s) => s.user)
  const isSchoolAdmin = String((currentUser as any)?.user_type ?? '').toUpperCase() === 'SCHOOL_ADMIN'

  const [locale,      setLocale]    = useState('En')
  // School admins can only target their own trainers.
  const [userType,    setUserType]  = useState(isSchoolAdmin ? 'TRAINER' : 'SCHOOL_ADMIN')
  const [selectedUsers, setSelectedUsers] = useState<{id: number; name: string}[]>([])
  const [userSearch,  setUserSearch] = useState('')
  const [dropOpen,    setDropOpen]  = useState(false)
  const [title,       setTitle]     = useState(editRow?.title ?? '')
  const [desc,        setDesc]      = useState(editRow?.description ?? '')
  const [body,        setBody]      = useState(editRow?.body ?? '')
  const [sendAll,     setSendAll]   = useState(false)
  const [loading,     setLoading]   = useState(false)
  const [error,       setError]     = useState('')
  const dropRef = useRef<HTMLDivElement>(null)

  const USER_TYPES = isSchoolAdmin
    ? [{ value: 'TRAINER', label: t('notificationPage.modal.userTypes.TRAINER') }]
    : [
        { value: 'STUDENT',      label: t('notificationPage.modal.userTypes.STUDENT')      },
        { value: 'TRAINER',      label: t('notificationPage.modal.userTypes.TRAINER')       },
        { value: 'SCHOOL_ADMIN', label: t('notificationPage.modal.userTypes.SCHOOL_ADMIN')  },
      ]
  const LOCALES = [
    { value: 'En', label: t('notificationPage.modal.locales.En') },
    { value: 'Ar', label: t('notificationPage.modal.locales.Ar') },
    { value: 'Ur', label: t('notificationPage.modal.locales.Ur') },
  ]

  // Fetch users based on selected userType — refetches automatically when userType changes.
  // School admins only see their own trainers (get-school-trainer-list).
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users-for-notification', userType, isSchoolAdmin],
    queryFn: () => restClient.get(
      isSchoolAdmin
        ? `${endpoints.school.schoolTrainers}?limit=100`
        : `${endpoints.users.list}?page=1&limit=1000&user_types[]=${userType}`
    ).then(r => r.data),
    enabled: open,
    staleTime: 60_000,
  })
  const allUsers: {id: number; name: string}[] = ((usersData?.data ?? []) as any[]).map((u: any) => ({
    id:   u.id ?? u.user_id ?? u.user?.id,
    name: u.name ?? u.user?.name ?? t('notificationPage.sentRow.trainerHash', { id: u.id ?? u.user_id ?? '' }),
  }))

  // Filter by search
  const filteredUsers = userSearch
    ? allUsers.filter(u => u.name?.toLowerCase().includes(userSearch.toLowerCase()))
    : allUsers

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropOpen) return
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropOpen])

  // Reset selected users when userType changes
  const handleUserTypeChange = (val: string) => {
    setUserType(val)
    setSelectedUsers([])
    setUserSearch('')
  }

  const toggleUser = (u: {id: number; name: string}) => {
    setSelectedUsers(prev =>
      prev.find(x => x.id === u.id)
        ? prev.filter(x => x.id !== u.id)
        : [...prev, u]
    )
  }

  const removeUser = (id: number) => setSelectedUsers(prev => prev.filter(x => x.id !== id))

  const handleSend = async () => {
    setError('')
    if (!title.trim()) { setError(t('notificationPage.modal.titleRequired')); return }
    if (!sendAll && selectedUsers.length === 0) { setError(t('notificationPage.modal.selectUserOrSendAll')); return }
    setLoading(true)
    try {
      await notificationService.send({
        title,
        description: desc,
        detail_message: body,
        user_type:  userType,
        locale,
        send_all:   sendAll ? 1 : 0,
        user_ids:   sendAll ? [] : selectedUsers.map(u => u.id),
      })
      onSuccess()
      onClose()
      setTitle(''); setDesc(''); setBody('')
      setSelectedUsers([]); setSendAll(false); setError('')
    } catch (e: any) {
      const msg = e?.errors
        ? Object.values(e.errors).flat().join(', ')
        : e?.message ?? t('notificationPage.modal.failedToSend')
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}/>
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">

        {/* Title */}
        <div className="px-8 pt-8 pb-6">
          <h2 className="text-xl font-black text-gray-900">{t('notificationPage.modal.sendNotification')}</h2>
        </div>

        {/* Form */}
        <div className="px-8 pb-8 space-y-4">

          {/* Row 1: Locale + User Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative">
              <label className="absolute -top-2 start-3 bg-white px-1 text-xs font-semibold text-gray-500">{t('notificationPage.modal.locale')}</label>
              <select value={locale} onChange={e=>setLocale(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-2xl px-4 py-4 pt-3 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white appearance-none">
                {LOCALES.map(l=><option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
              <div className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-gray-400">▾</div>
            </div>
            <div className="relative">
              <label className="absolute -top-2 start-3 bg-white px-1 text-xs font-semibold text-gray-500">{t('notificationPage.modal.userType')}</label>
              <select value={userType} onChange={e=>handleUserTypeChange(e.target.value)}
                disabled={isSchoolAdmin}
                className={cn(
                  'w-full text-sm border border-gray-200 rounded-2xl px-4 py-4 pt-3 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white appearance-none font-bold',
                  isSchoolAdmin && 'opacity-60 cursor-not-allowed'
                )}>
                {USER_TYPES.map(ut=><option key={ut.value} value={ut.value}>{ut.label}</option>)}
              </select>
              <div className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-gray-400">▾</div>
            </div>
          </div>

          {/* Row 2: User IDs multi-select + Notification Title */}
          <div className="grid grid-cols-2 gap-4">

            {/* User IDs — searchable multi-select dropdown */}
            <div className="relative" ref={dropRef}>
              <label className="absolute -top-2 start-3 bg-white px-1 text-xs font-semibold text-gray-500 z-10">{t('notificationPage.modal.userIds')}</label>
              {/* Trigger */}
              <div
                onClick={()=>!sendAll && setDropOpen(o=>!o)}
                className={cn(
                  'w-full border border-gray-200 rounded-2xl px-3 py-2.5 min-h-[52px] cursor-pointer flex flex-wrap gap-1 items-start',
                  sendAll && 'opacity-40 cursor-not-allowed',
                  dropOpen && 'ring-2 ring-gray-300'
                )}>
                {selectedUsers.length === 0 && !dropOpen && (
                  <span className="text-sm text-gray-300 self-center">{t('notificationPage.modal.selectUsers')}</span>
                )}
                {selectedUsers.map(u=>(
                  <span key={u.id} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                    {u.name}
                    <button type="button" onClick={e=>{e.stopPropagation();removeUser(u.id)}}
                      className="text-gray-400 hover:text-red-500 leading-none">×</button>
                  </span>
                ))}
                <span className="ms-auto self-center text-gray-400 text-sm flex-shrink-0">▾</span>
              </div>

              {/* Dropdown */}
              {dropOpen && !sendAll && (
                <div className="absolute start-0 end-0 top-full mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg z-20 overflow-hidden">
                  {/* Search inside dropdown */}
                  <div className="px-3 py-2 border-b border-gray-100">
                    <input
                      autoFocus
                      value={userSearch}
                      onChange={e=>setUserSearch(e.target.value)}
                      placeholder={t('notificationPage.modal.search')}
                      className="w-full text-xs border border-gray-200 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {usersLoading ? (
                      <div className="px-4 py-3 text-xs text-gray-400">{t('notificationPage.modal.loadingUsers', { type: userType })}</div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="px-4 py-3 text-xs text-gray-400">{t('notificationPage.modal.noUsersFound')}</div>
                    ) : filteredUsers.map(u=>(
                      <div key={u.id}
                        onClick={()=>toggleUser(u)}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-brand-50 text-sm transition-colors',
                          selectedUsers.find(x=>x.id===u.id) && 'bg-brand-50 font-semibold'
                        )}>
                        <div className={cn(
                          'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0',
                          selectedUsers.find(x=>x.id===u.id)
                            ? 'bg-brand-500 border-brand-500'
                            : 'border-gray-300'
                        )}>
                          {selectedUsers.find(x=>x.id===u.id) && (
                            <span className="text-white text-[12px] font-black">✓</span>
                          )}
                        </div>
                        <span className="truncate">{u.name}</span>
                        <span className="ms-auto text-[12px] text-gray-400 flex-shrink-0">#{u.id}</span>
                      </div>
                    ))}
                  </div>
                  {selectedUsers.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-100 text-xs text-brand-600 font-semibold">
                      {t('notificationPage.modal.usersSelected', { n: selectedUsers.length })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <label className="absolute -top-2 start-3 bg-white px-1 text-xs font-semibold text-gray-500">{t('notificationPage.modal.notificationTitle')}</label>
              <input value={title} onChange={e=>setTitle(e.target.value)}
                placeholder={t('notificationPage.modal.notificationTitle')}
                className="w-full text-sm border border-gray-200 rounded-2xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-gray-300 placeholder-gray-300"/>
            </div>
          </div>

          {/* Row 3: Description + Body */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative">
              <label className="absolute -top-2 start-3 bg-white px-1 text-xs font-semibold text-gray-500">{t('notificationPage.modal.description')}</label>
              <textarea value={desc} onChange={e=>setDesc(e.target.value)}
                rows={4} placeholder={t('notificationPage.modal.description')}
                className="w-full text-sm border border-gray-200 rounded-2xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none placeholder-gray-300"/>
            </div>
            <div className="relative">
              <label className="absolute -top-2 start-3 bg-white px-1 text-xs font-semibold text-gray-500">{t('notificationPage.modal.body')}</label>
              <textarea value={body} onChange={e=>setBody(e.target.value)}
                rows={4} placeholder={t('notificationPage.modal.body')}
                className="w-full text-sm border border-gray-200 rounded-2xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none placeholder-gray-300"/>
            </div>
          </div>

          {/* Send to all users toggle */}
          <div className="flex items-center gap-3 pt-1">
            <span className="text-sm text-gray-700 font-medium">{t('notificationPage.modal.sendToAll')}</span>
            <button
              type="button"
              onClick={()=>{ setSendAll(v=>!v); setSelectedUsers([]) }}
              className={cn(
                'relative inline-flex w-11 h-6 rounded-full transition-colors flex-shrink-0',
                sendAll ? 'bg-gray-800' : 'bg-gray-200'
              )}>
              <span className={cn(
                'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
                sendAll ? 'translate-x-5' : 'translate-x-0'
              )}/>
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 font-semibold">
              {error}
            </div>
          )}

          {/* Send button */}
          <div className="flex justify-end pt-2">
            <button onClick={handleSend} disabled={loading}
              className="px-8 py-3.5 rounded-2xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold transition-colors disabled:opacity-50">
              {loading ? t('notificationPage.modal.sending') : t('notificationPage.modal.sendNotification')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Paginator ─────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage, onPerPage }: {
  page: number; lastPage: number; total: number; perPage: number
  onPage: (p: number) => void; onPerPage: (n: number) => void
}) {
  const { t } = useTranslation()
  const nums = (() => {
    let s = Math.max(1, page-2); if(s+4>lastPage) s=Math.max(1,lastPage-4)
    return Array.from({length:Math.min(5,lastPage)},(_,i)=>s+i).filter(p=>p<=lastPage)
  })()
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{t('notificationPage.paginator.itemsPerPage')}</span>
        <select value={perPage} onChange={e=>onPerPage(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400">
          {[10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <span>{t('notificationPage.paginator.itemsRange', { start: (page-1)*perPage+1, end: Math.min(page*perPage,total), total })}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={()=>onPage(Math.max(1,page-1))} disabled={page<=1}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft size={14} className="rtl:rotate-180"/>
        </button>
        {nums.map(p=>(
          <button key={p} onClick={()=>onPage(p)}
            className={cn('w-8 h-8 rounded-lg border text-xs font-semibold',
              p===page?'bg-brand-500 text-white border-brand-500':'border-gray-200 text-gray-600 hover:bg-gray-50')}>
            {p}
          </button>
        ))}
        <button onClick={()=>onPage(Math.min(lastPage,page+1))} disabled={page>=lastPage}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronRight size={14} className="rtl:rotate-180"/>
        </button>
        <span className="ms-1 text-xs text-gray-400">{t('notificationPage.paginator.ofPages', { n: lastPage })}</span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function NotificationPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const currentUser   = useAuthStore((s) => s.user)
  const isSchoolAdmin = String((currentUser as any)?.user_type ?? '').toUpperCase() === 'SCHOOL_ADMIN'
  const [search,     setSearch]     = useState('')
  const [page,       setPage]       = useState(1)
  const [perPage,    setPerPage]    = useState(20)
  const [modalOpen,  setModalOpen]  = useState(false)
  const [editRow,    setEditRow]    = useState<any>(null)

  const debouncedSearch = useDebounce(search)

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', page, perPage, debouncedSearch],
    queryFn: () => notificationService.list({ page, limit: perPage, search: debouncedSearch || undefined }),
    staleTime: 30_000,
  })

  const rows: any[]  = (data as any)?.data ?? []
  const total        = (data as any)?.meta?.total ?? (data as any)?.total ?? rows.length
  const lastPage     = Math.ceil(total / perPage) || 1

  const handleExport = () => {
    const csv = [[t('notificationPage.csv.id'),t('notificationPage.csv.title'),t('notificationPage.csv.event'),t('notificationPage.csv.user'),t('notificationPage.csv.readAt'),t('notificationPage.csv.createdAt')],
      ...rows.map((r:any)=>[r.id, r.title??'', r.trigger_event??'', r.user_id??'', r.read_at??'', r.created_at??''])
    ].map(r=>r.join(',')).join('\n')
    const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='notifications.csv'; a.click()
  }

  const handleSuccess = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['notifications'] })
  }, [qc])


  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span>{t('notificationPage.breadcrumbDashboard')}</span><span>/</span>
            <span className="text-brand-500 font-semibold">{isSchoolAdmin ? t('notificationPage.titleSchool') : t('notificationPage.titleAdmin')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{isSchoolAdmin ? t('notificationPage.titleSchool') : t('notificationPage.titleAdmin')}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isSchoolAdmin && (
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
              <Download size={12}/> {t('notificationPage.download')}
            </button>
          )}
          {/* Search — filter by user name */}
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-44">
            <Search size={12} className="text-gray-400 flex-shrink-0"/>
            <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}}
              placeholder={t('notificationPage.searchByName')}
              className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0"/>
            {search && <button onClick={()=>setSearch('')}><X size={11} className="text-gray-300 hover:text-gray-500"/></button>}
          </div>
          {/* Create */}
          <button onClick={()=>{ setEditRow(null); setModalOpen(true) }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-colors">
            <Plus size={13}/> {isSchoolAdmin ? t('notificationPage.createSchool') : t('notificationPage.createNewAdmin')}
          </button>
        </div>
      </div>

      {/* ── Message list (school admin) / Card grid (admin) ── */}
      {isSchoolAdmin ? (
        <div className="space-y-3">
          {isLoading
            ? Array.from({length:5}).map((_,i)=>(
                <div key={i} className="h-24 bg-white rounded-2xl border border-gray-200 animate-pulse"/>
              ))
            : rows.length === 0
              ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Send size={24} className="text-gray-300"/>
                  </div>
                  <p className="text-sm font-semibold text-gray-500">{t('notificationPage.emptyNoSent')}</p>
                </div>
              )
              : rows.map((row:any) => (
                <SentMessageRow key={row.id} row={row}/>
              ))
          }
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {isLoading
            ? Array.from({length:6}).map((_,i)=><SkeletonCard key={i}/>)
            : rows.length === 0
              ? (
                <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Bell size={24} className="text-gray-300"/>
                  </div>
                  <p className="text-sm font-semibold text-gray-500">{t('notificationPage.emptyNoNotifications')}</p>
                </div>
              )
              : rows.map((row:any) => (
                <NotificationCard key={row.id} row={row}/>
              ))
          }
        </div>
      )}

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage}
          onPage={setPage} onPerPage={n=>{setPerPage(n);setPage(1)}}/>
      )}

      {/* ── Create / Edit Modal ── */}
      <CreateNotificationModal
        open={modalOpen}
        onClose={()=>setModalOpen(false)}
        onSuccess={handleSuccess}
        editRow={editRow}
      />
    </div>
  )
}
